/**
 * Audit-capture singleton — Phase 7.1 in-memory + Phase 7.3 persistent
 * (localStorage) + Phase 7.4 per-tenant separation.
 *
 * Per the design doc:
 *   - Append-only (no delete, no mutate)
 *   - Synchronous emit — Phase 7.6+ will batch+queue with 5ms p99 budget
 *   - Anonymous actor (userId stable per-session, never tied to PII)
 *   - Snapshot id captured so the audit panel can reconstruct the world
 *     state at event time
 *   - Storage backend selected at module-load: browser → LocalStorageStore,
 *     SSR/incognito-quota-error → MemoryStore. Phase 7.5 ImmutableStore
 *     wired via __setAuditStore() at deployment-config time.
 *
 * Per pii-redaction.md: this module NEVER logs annotation contents,
 * briefing payloads, or any field on the never-list. The `payload` block
 * on each event is the consumer's responsibility — if you emit
 * `annotation.create`, pass the annotation id, NOT the annotation text.
 */

import type { AuditEvent, AuditCapture, AuditActorRole } from './types';
import { selectDefaultStore, type AuditStore } from './storage';

// Note: makeId + monotonicCounter declared FIRST so module-load const
// initialisations (SESSION_ID below) can call makeId without hitting a
// temporal-dead-zone on the `let`. Function declarations hoist; `let`
// bindings do not initialise until their declaration line runs.
let monotonicCounter = 0;
function makeId(): string {
  monotonicCounter = (monotonicCounter + 1) & 0xffff;
  const ts = Date.now().toString(36);
  const rnd = Math.random().toString(36).slice(2, 8);
  const ctr = monotonicCounter.toString(36).padStart(3, '0');
  return `${ts}-${rnd}-${ctr}`;
}

/** Anonymous per-session id, regenerated per page load. */
const SESSION_ID = makeId();

/** Anonymous per-user id. Demo default; production reads from auth. */
const USER_ID = 'demo-operator-001';

/** Phase 7.4 per-tenant context. Single-tenant default for the demo;
 *  production deployments call setTenantContext() at login to scope
 *  every subsequent event + audit-panel read. */
let CURRENT_TENANT: string = 'demo-tenant';
let CURRENT_ROLE: AuditActorRole = 'duty-epi';

export function setTenantContext(ctx: { tenant: string; role?: AuditActorRole }): void {
  CURRENT_TENANT = ctx.tenant;
  if (ctx.role) CURRENT_ROLE = ctx.role;
}
export function getTenantContext(): { tenant: string; role: AuditActorRole } {
  return { tenant: CURRENT_TENANT, role: CURRENT_ROLE };
}

/** Phase 7.5 deployment hook. Production wires an ImmutableStore impl
 *  here before any events are emitted. The default LocalStorageStore is
 *  best-effort browser persistence — NOT regulator-grade retention. */
let store: AuditStore = selectDefaultStore();
export function __setAuditStore(next: AuditStore): void { store = next; }
export function getActiveStoreLabel(): string { return store.label; }

function currentContext(): AuditEvent['context'] {
  const snapStub = `v0.3-${typeof window !== 'undefined' ? window.location.hash.slice(0, 32) : 'server'}-${new Date().toISOString().slice(0, 13)}`;
  return {
    snapshotId: snapStub,
    feedVersion: '0.3.0',
    appVersion: 'unknown',
  };
}

export const auditCapture: AuditCapture = {
  emit(partial) {
    const full: AuditEvent = {
      id: makeId(),
      asOf: new Date().toISOString(),
      actor: {
        userId: USER_ID,
        sessionId: SESSION_ID,
        role: CURRENT_ROLE,
        tenant: CURRENT_TENANT,
      },
      context: currentContext(),
      event: partial.event,
    };
    // Fire-and-forget — Phase 7.6 will batch + handle backpressure.
    void Promise.resolve(store.append(full));
  },
  list(opts) {
    return store.list(CURRENT_TENANT, opts);
  },
  __reset() {
    store.__reset(CURRENT_TENANT);
  },
};
